import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Compass, TrendingUp, Target, Check, X } from "lucide-react";
import LearningCycle from "@/components/LearningCycle";
import Logo from "@/components/Logo";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex">
          <Logo variant="name" height={52} testid="landing-header-logo" />
        </Link>
        <nav className="flex items-center gap-3">
          <Link to="/anmelden"><Button variant="ghost" className="text-sm" data-testid="landing-login-link">Anmelden</Button></Link>
          <Link to="/registrieren"><Button className="rounded-full h-10 px-5" data-testid="landing-register-btn">Kostenlos starten</Button></Link>
        </nav>
      </header>

      {/* Akt 1 */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-20 text-center">
        <div className="flex justify-center mb-8">
          <Logo variant="full" height={280} testid="landing-hero-logo" />
        </div>
        <h1 className="font-heading text-[32px] md:text-5xl leading-[1.15] tracking-tight">
          Die meisten Trader sammeln Trades.<br />
          <span className="text-muted-foreground">Die wenigsten lernen wirklich aus ihnen.</span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-muted-foreground">Genau dort beginnt unsere Plattform.</p>
      </section>

      {/* Akt 2 */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 border-t border-border">
        <div className="overline mb-4">Du bist nicht allein.</div>
        <div className="space-y-4 text-lg leading-relaxed">
          <p>Du hast Strategien ausprobiert.</p>
          <p>Du hast Videos geschaut.</p>
          <p>Du hast Bücher gelesen.</p>
          <p>Vielleicht führst du bereits ein Journal.</p>
          <p className="text-muted-foreground">Trotzdem fehlt dir Klarheit.</p>
        </div>
      </section>

      {/* Akt 3 */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 border-t border-border">
        <div className="overline mb-4">Vielleicht liegt das eigentliche Problem woanders.</div>
        <p className="text-2xl md:text-3xl font-heading tracking-tight leading-tight">
          Nicht fehlendes Wissen.<br />
          <span className="text-primary-foreground/70">Sondern fehlende Reflexion.</span>
        </p>
      </section>

      {/* Akt 4 */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-16 border-t border-border">
        <div className="overline mb-4">Unser Ansatz</div>
        <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8">Der Lernkreislauf</h2>
        <div className="rounded-2xl border border-border bg-card p-6 md:p-10">
          <LearningCycle />
          <p className="text-sm text-muted-foreground text-center mt-8 max-w-md mx-auto leading-relaxed">
            Jede Runde macht dich präziser. Aus Reflexion wird Anpassung — und aus Anpassung eine bessere nächste Runde.
          </p>
        </div>
      </section>

      {/* Akt 5 */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 border-t border-border">
        <div className="overline mb-4 text-center">Wo stehst du gerade?</div>
        <p className="text-sm text-muted-foreground text-center mb-8 max-w-lg mx-auto">
          Wähle deinen Ausgangspunkt — wir richten deinen Start passend aus.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4">
          {[
            { slug: "orientierung", Icon: Compass, title: "Orientierung", text: "Für Trader, die ihr Fundament aufbauen möchten." },
            { slug: "struktur", Icon: TrendingUp, title: "Struktur", text: "Für Trader, die ihre Erfahrung strukturieren möchten." },
            { slug: "optimierung", Icon: Target, title: "Optimierung", text: "Für Trader, die bereits erfolgreich handeln und ihre Performance gezielt weiterentwickeln möchten." },
          ].map(({ slug, Icon, title, text }) => (
            <Link
              key={slug}
              to={`/registrieren?stage=${slug}`}
              className="group rounded-2xl border border-border bg-card p-6 text-left transition-all hover:border-primary/50 hover:bg-card/80 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid={`landing-stage-${slug}`}
            >
              <Icon className="h-5 w-5 text-primary-foreground/60 group-hover:text-primary transition-colors" strokeWidth={1.5} />
              <div className="font-heading text-lg mt-4">{title}</div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{text}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                Hier starten <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Akt 6 */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20 border-t border-border" id="preise">
        <div className="text-center mb-12">
          <div className="overline mb-4">Preise</div>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight">
            30 Tage kostenlos testen.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            Start mit voller Funktion — nach 30 Tagen entscheidest du, ob du in die kostenlose
            Standardversion wechselst oder die Vollversion behältst.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Standardversion */}
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 flex flex-col" data-testid="pricing-standard">
            <div className="overline">Standardversion</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-heading text-4xl">0 €</span>
              <span className="text-sm text-muted-foreground">dauerhaft kostenlos</span>
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              Der ruhige Einstieg — genug, um dein Trading zu strukturieren.
            </p>
            <ul className="mt-6 space-y-3 text-sm flex-1">
              <PriceFeature yes>Trading-Journal mit Basis-Analyse</PriceFeature>
              <PriceFeature yes>Bis zu 2 gespeicherte Strategien</PriceFeature>
              <PriceFeature yes>Bis zu 50 Trade-Einträge pro Jahr</PriceFeature>
              <PriceFeature yes>KI-Coach mit eingeschränktem Kontingent</PriceFeature>
              <PriceFeature>Steuer-Tool und PDF-Export</PriceFeature>
              <PriceFeature>Broker-Sync für alle 5 Börsen</PriceFeature>
              <PriceFeature>Unbegrenzte Trades und Strategien</PriceFeature>
            </ul>
            <Link to="/registrieren" className="mt-6">
              <Button variant="outline" className="w-full rounded-full h-11" data-testid="pricing-standard-cta">
                Kostenlos starten
              </Button>
            </Link>
          </div>

          {/* Vollversion */}
          <div className="rounded-2xl border-2 border-primary/60 bg-card p-6 md:p-8 flex flex-col relative" data-testid="pricing-vollversion">
            <div className="absolute -top-3 left-8 rounded-full bg-primary text-primary-foreground text-xs font-medium px-3 py-1">
              30 Tage gratis
            </div>
            <div className="overline text-primary">Vollversion</div>
            <div className="mt-3 flex items-baseline gap-2 flex-wrap">
              <span className="font-heading text-4xl">8,99 €</span>
              <span className="text-sm text-muted-foreground">pro Monat</span>
              <span className="text-xs text-muted-foreground/70 ml-2">oder 84,99 € / Jahr</span>
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              Der ganze Prozess — Reflexion, Muster, Steuer, alles an einem Ort.
            </p>
            <ul className="mt-6 space-y-3 text-sm flex-1">
              <PriceFeature yes>Alles aus der Standardversion</PriceFeature>
              <PriceFeature yes>Unbegrenzte Trades und Strategien</PriceFeature>
              <PriceFeature yes>Steuer-Tool mit PDF-Bericht (§ 23 & § 20 EStG)</PriceFeature>
              <PriceFeature yes>Voller KI-Coach ohne Limit</PriceFeature>
              <PriceFeature yes>Broker-Sync für OKX, Bitget, Binance, Kraken, Bitunix</PriceFeature>
              <PriceFeature yes>Positions-Aggregation und Sub-Account-KPIs</PriceFeature>
              <PriceFeature yes>Monatlich kündbar, keine Vertragslaufzeit</PriceFeature>
            </ul>
            <Link to="/registrieren" className="mt-6">
              <Button className="w-full rounded-full h-11" data-testid="pricing-vollversion-cta">
                30 Tage kostenlos testen <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Kein Zahlungsmittel für die Testphase erforderlich.
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-6">
          Preise inkl. gesetzlicher USt., Abwicklung sicher über Stripe. Zahlung mit
          Kreditkarte, SEPA-Lastschrift, PayPal oder Stripe Link. Kündigung jederzeit im Kundenportal.
        </p>
      </section>

      {/* Akt 7 */}
      <section className="max-w-2xl mx-auto px-6 md:px-10 py-20 border-t border-border text-center">
        <div className="overline mb-4">Bereit?</div>
        <h2 className="font-heading text-3xl md:text-4xl tracking-tight">Dann fangen wir gemeinsam an.</h2>
        <p className="mt-4 text-muted-foreground">Kostenlos starten. Jederzeit kündbar.</p>
        <Link to="/registrieren">
          <Button className="mt-8 rounded-full h-12 px-6" data-testid="landing-cta-register">
            Kostenlos starten <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
        <p className="mt-6 text-xs text-muted-foreground">
          Wir treffen keine Handelsentscheidungen. Keine automatischen Trades. Deine Daten bleiben bei dir.
        </p>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div>© 2026 TradeMind</div>
          <nav className="flex items-center gap-6">
            <Link to="/impressum" className="hover:text-foreground">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-foreground">Datenschutz</Link>
            <Link to="/agb" className="hover:text-foreground">AGB</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function PriceFeature({ children, yes }) {
  return (
    <li className="flex items-start gap-2">
      {yes ? (
        <Check className="h-4 w-4 text-[hsl(122_39%_65%)] mt-0.5 shrink-0" strokeWidth={2} />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/50 mt-0.5 shrink-0" strokeWidth={1.5} />
      )}
      <span className={yes ? "" : "text-muted-foreground/70 line-through"}>{children}</span>
    </li>
  );
}
